import { create } from 'zustand'
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { getSubscriptionStatus, type SubscriptionStatus } from '@/api/subscription'

const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true'
const DEV_USER_EMAIL =
  (import.meta.env.VITE_DEV_USER_EMAIL as string | undefined) ?? 'dev@example.com'

// Fake user for dev mode - matches backend's dev-user-123
const DEV_USER: User = {
  id: 'dev-user-123',
  email: DEV_USER_EMAIL,
  app_metadata: { provider: 'dev' },
  user_metadata: { name: 'Dev User' },
  aud: 'authenticated',
  created_at: new Date().toISOString(),
} as User

export interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  isInitialized: boolean
  authMethod: 'google' | 'email' | 'dev' | null
  pendingEmail: string | null
  error: string | null
  isDevMode: boolean
  // Subscription state
  subscription: SubscriptionStatus | null
  isSubscriptionLoading: boolean

  // Actions
  initialize: () => Promise<void>
  devLogin: () => void
  signInWithGoogle: () => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updatePassword: (password: string) => Promise<void>
  resendConfirmation: (email: string) => Promise<void>
  clearError: () => void
  setPendingEmail: (email: string | null) => void
  handleAuthStateChange: (event: AuthChangeEvent, session: Session | null) => void
  // Subscription actions
  fetchSubscription: () => Promise<void>
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  session: null,
  isLoading: true,
  isInitialized: false,
  authMethod: null,
  pendingEmail: null,
  error: null,
  isDevMode: DEV_MODE,
  // Subscription state
  subscription: null,
  isSubscriptionLoading: false,

  initialize: async () => {
    try {
      console.log('[authStore] initialize: Starting (DEV_MODE:', DEV_MODE, ')')
      set({ isLoading: true, error: null })

      // In dev mode, auto-login as dev user (unless explicitly logged out)
      if (DEV_MODE) {
        const wasLoggedOut = sessionStorage.getItem('dev-logged-out') === 'true'
        console.log('[authStore] initialize: Dev mode - wasLoggedOut:', wasLoggedOut)
        if (!wasLoggedOut) {
          console.log('[authStore] initialize: Auto-logging in as dev user')
          set({
            user: DEV_USER,
            session: null,
            authMethod: 'dev',
            isInitialized: true,
            isLoading: false,
          })
          return
        } else {
          console.log('[authStore] initialize: User was logged out, not auto-logging in')
        }
      }

      // Get the current session from Supabase
      console.log('[authStore] initialize: Checking Supabase session')
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) {
        console.error('[authStore] initialize: Error getting session:', error.message)
        set({ error: error.message })
        return
      }

      // Determine auth method from session
      let authMethod: AuthState['authMethod'] = null
      if (session?.user) {
        const provider = session.user.app_metadata?.provider
        if (provider === 'google') {
          authMethod = 'google'
        } else {
          authMethod = 'email'
        }
      }

      console.log('[authStore] initialize: Setting state - user:', session?.user?.email || 'null')
      set({
        session,
        user: session?.user ?? null,
        authMethod,
        isInitialized: true,
      })
    } catch (err) {
      console.error('[authStore] initialize: Error:', err)
      set({ error: 'Failed to initialize authentication' })
    } finally {
      set({ isLoading: false })
    }
  },

  devLogin: () => {
    if (!DEV_MODE) return
    sessionStorage.removeItem('dev-logged-out')
    set({
      user: DEV_USER,
      session: null,
      authMethod: 'dev',
      isLoading: false,
      error: null,
    })
  },

  handleAuthStateChange: (event: AuthChangeEvent, session: Session | null) => {
    // Determine auth method
    let authMethod: AuthState['authMethod'] = null
    if (session?.user) {
      const provider = session.user.app_metadata?.provider
      if (provider === 'google') {
        authMethod = 'google'
      } else {
        authMethod = 'email'
      }
    }

    switch (event) {
      case 'SIGNED_IN':
        set({
          session,
          user: session?.user ?? null,
          authMethod,
          isLoading: false,
          error: null,
        })
        break

      case 'SIGNED_OUT':
        set({
          session: null,
          user: null,
          authMethod: null,
          isLoading: false,
          error: null,
          pendingEmail: null,
          subscription: null,
        })
        break

      case 'TOKEN_REFRESHED':
        set({
          session,
          user: session?.user ?? null,
        })
        break

      case 'PASSWORD_RECOVERY':
        // User clicked password reset link in email
        set({
          session,
          user: session?.user ?? null,
        })
        break

      case 'USER_UPDATED':
        set({
          session,
          user: session?.user ?? null,
        })
        break

      default:
        // Handle other events
        if (session) {
          set({
            session,
            user: session.user,
            authMethod,
          })
        }
        break
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      })

      if (error) {
        throw error
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in with Google'
      console.error('Error signing in with Google:', message)
      set({ error: message, isLoading: false })
      throw err
    }
  },

  signUp: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/signup/confirm`,
        },
      })

      if (error) {
        throw error
      }

      // Store email for verification page
      set({ pendingEmail: email, authMethod: 'email' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign up'
      console.error('Error signing up:', message)
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null })

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        throw error
      }

      set({
        session: data.session,
        user: data.user,
        authMethod: 'email',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in'
      console.error('Error signing in:', message)
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  signOut: async () => {
    try {
      console.log('[authStore] signOut: Starting (DEV_MODE:', DEV_MODE, ')')
      set({ isLoading: true, error: null })

      // In dev mode, mark as logged out in session storage FIRST
      if (DEV_MODE) {
        console.log('[authStore] signOut: Dev mode - setting session storage BEFORE clearing state')
        sessionStorage.setItem('dev-logged-out', 'true')
        console.log(
          '[authStore] signOut: Session storage set. Value:',
          sessionStorage.getItem('dev-logged-out')
        )
      }

      // Clear state (this will trigger React re-renders)
      console.log('[authStore] signOut: Clearing user state')
      set({
        session: null,
        user: null,
        authMethod: null,
        pendingEmail: null,
        isLoading: false,
        subscription: null,
      })
      console.log('[authStore] signOut: State cleared - user is now null')

      if (DEV_MODE) {
        console.log('[authStore] signOut: Dev mode signout complete')
        return
      }

      // In production, call Supabase signOut (will trigger auth state change event)
      console.log('[authStore] signOut: Production mode - calling Supabase signOut')
      const { error } = await supabase.auth.signOut()

      if (error) {
        throw error
      }

      console.log('[authStore] signOut: Production mode signout complete')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign out'
      console.error('[authStore] signOut: Error:', message)
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  resetPassword: async (email: string) => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        throw error
      }

      set({ pendingEmail: email })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email'
      console.error('Error resetting password:', message)
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updatePassword: async (password: string) => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase.auth.updateUser({
        password,
      })

      if (error) {
        throw error
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update password'
      console.error('Error updating password:', message)
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  resendConfirmation: async (email: string) => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/signup/confirm`,
        },
      })

      if (error) {
        throw error
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend confirmation email'
      console.error('Error resending confirmation:', message)
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  clearError: () => {
    set({ error: null })
  },

  setPendingEmail: (email: string | null) => {
    set({ pendingEmail: email })
  },

  fetchSubscription: async () => {
    const { user, isDevMode } = get()

    // In dev mode, always grant access
    if (isDevMode) {
      set({
        subscription: {
          has_access: true,
          is_grandfathered: false,
          subscription_status: null,
          subscription_price_id: null,
          subscription_period_end: null,
        },
        isSubscriptionLoading: false,
      })
      return
    }

    // No user = no subscription to fetch
    if (!user) {
      set({ subscription: null, isSubscriptionLoading: false })
      return
    }

    try {
      set({ isSubscriptionLoading: true })
      const status = await getSubscriptionStatus()
      set({ subscription: status, isSubscriptionLoading: false })
    } catch (error) {
      console.error('[authStore] fetchSubscription error:', error)
      // On error, assume no access to be safe
      set({
        subscription: {
          has_access: false,
          is_grandfathered: false,
          subscription_status: null,
          subscription_price_id: null,
          subscription_period_end: null,
        },
        isSubscriptionLoading: false,
      })
    }
  },
}))
